import React, { useEffect, useState, useMemo, useRef } from "react";
import Navlinks from "./navlinks";
import { Bell, Menu, X } from "lucide-react";
import UserAvatar from "./user-avatar";
import { Logo } from "../../constant/imgages";
import { adminLinks, customLinks, employeeLinks } from "../../utils/links";
import { useLocation, useNavigate } from "react-router-dom";
import { Dropdown, notification } from "antd";
import { User, LogOut } from "lucide-react";
import NotificationModal from "../../modals/notification";
import ChangePasswordModal from "../../modals/changePassword";
import { getEmployeeData } from "../../utils/localStorage"
import { getRequest, getRequestSSE  } from "../../service/index";


// Helper to check if all child arrays in a permission section are empty
const isAllChildrenEmpty = (section) => {
  if (!section || typeof section !== "object") return true;
  return Object.values(section).every(
    (child) => Array.isArray(child) && child.length === 0
  );
};

const Navbar = () => {

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [width, setWidth] = useState(undefined);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const sseControllerRef = useRef(null);
  const previousUnreadCountRef = useRef(0);
  const unreadRequestRef = useRef(null);
  const isOnlineRef = useRef(true);
  const processedNotificationsRef = useRef(new Set());
  const lastNotificationHashRef = useRef('');
  
  useEffect(() => {
    const checkAuth = () => {
      const data = localStorage.getItem("data");
      const token = localStorage.getItem("token");
      
      if (!data || !token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        navigate("/");
        return;
      }
      
      try {
        const parsedData = JSON.parse(data);
        if (!parsedData || !parsedData.user) {
          setIsAuthenticated(false);
          setIsLoading(false);
          navigate("/");
          return;
        }
        
        // If we reach here, user is authenticated
        setIsAuthenticated(true);
        setIsLoading(false);
      } catch (error) {
        console.error("Error parsing localStorage data:", error);
        setIsAuthenticated(false);
        setIsLoading(false);
        navigate("/");
        return;
      }
    };
    
    checkAuth();
  }, [navigate]);

  const data = getEmployeeData();
  
  const filteredCustomLinks = useMemo(() => {
    if (!data?.designation?.permissions) return customLinks;

    const permissions = data.designation.permissions;

    // Map customLinks label to permission key
    const labelToPermissionKey = {
      Organization: "Organization",
      Employees: null,
      Attendance: null,
      Leave: null,
      Payroll: null,
      Dashboard: null, 
    };

    return customLinks.filter((link) => {
      const permKey = labelToPermissionKey[link.label];
      if (!permKey) return true; // Dashboard or unknown, always show

      const section = permissions[permKey];
      if (!section) return false;
      if (isAllChildrenEmpty(section)) return false;
      return true;
    });
  }, [data]);

  // Correct navlinks logic based on user role
  const navlinks = useMemo(() => {
    const userRole = data?.role;
    if (userRole === "companyadmin") {
      return adminLinks;
    } else if (userRole === "employee") {
      return employeeLinks;
    } else {
      return filteredCustomLinks;
    }
  }, [data, filteredCustomLinks]);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleLogout = () => {
    localStorage.removeItem("data");
    localStorage.removeItem("token");
    navigate("/");
  };

  const items = [
    {
      key: "profile",
      label: "Change Password",
      icon: <User className="h-4 w-4" />,
      onClick: () => setIsChangePasswordOpen(true),
    },
    {
      key: "logout",
      label: "Logout",
      icon: <LogOut className="h-4 w-4" />,
      onClick: handleLogout,
    },
  ];

  useEffect(() => {
    setIsMenuOpen(false); // Close menu on route change
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
  }, [isMenuOpen]);

  useEffect(() => {
    const onResize = () => {
      setWidth(window.innerWidth);
    };
    window.addEventListener("resize", onResize);
    onResize(); // Initialize width on mount

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Helper function to create a hash for notification data
  const createNotificationHash = (notifications) => {
    if (!Array.isArray(notifications) || notifications.length === 0) return '';
    return notifications.map(n => n._id).sort().join(',');
  };

  // Debounced fetch unread count function
  const fetchUnreadCount = useRef(async () => {
    // Cancel any existing request
    if (unreadRequestRef.current) {
      try {
        unreadRequestRef.current.abort();
      } catch (e) {
        // ignore
      }
    }

    // Create new AbortController for this request
    const controller = new AbortController();
    unreadRequestRef.current = controller;

    try {
      const result = await getRequest(`/api/notifications/unread`, { signal: controller.signal });
      if (typeof result?.unreadCount === 'number') {
        const previousCount = previousUnreadCountRef.current;
        setUnreadCount(result.unreadCount);
        
        // Show toast for new notification if count increased and we have latest notification
        if (result.unreadCount > previousCount && previousCount > 0 && result.latest) {
          const { type, senderName, priority = 'medium' } = result.latest;
          
          // Get priority-based colors
          const getPriorityStyle = (priority) => {
            switch (priority) {
              case 'low':
                return { backgroundColor: '#fff7e6', borderColor: '#faad14', color: '#d48806' };
              case 'medium':
                return { backgroundColor: '#f6ffed', borderColor: '#52c41a', color: '#389e0d' };
              case 'high':
                return { backgroundColor: '#fff2f0', borderColor: '#ff4d4f', color: '#cf1322' };
              default:
                return { backgroundColor: '#f6ffed', borderColor: '#52c41a', color: '#389e0d' };
            }
          };
          
          const priorityStyle = getPriorityStyle(priority);
          
          notification.info({
            message: type,
            description: senderName,
            placement: 'topRight',
            duration: 4,
            style: {
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: `2px solid ${priorityStyle.borderColor}`,
              backgroundColor: priorityStyle.backgroundColor,
              color: priorityStyle.color,
            },
          });
        }
        
        // Update the previous count ref
        previousUnreadCountRef.current = result.unreadCount;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // Request was cancelled, ignore
        return;
      }
      console.error('Error fetching unread count:', error);
    } finally {
      if (unreadRequestRef.current === controller) {
        unreadRequestRef.current = null;
      }
    }
  });

  // Fetch unread count on mount and when storage updates
  useEffect(() => {
    // Initial fetch
    fetchUnreadCount.current();
    
    // Set up periodic check for new notifications (every 30 seconds instead of 10)
    const intervalId = setInterval(() => {
      if (isOnlineRef.current) {
        fetchUnreadCount.current();
      }
    }, 30000);
    
    const onStorageUpdate = () => {
      if (isOnlineRef.current) {
        fetchUnreadCount.current();
      }
    };
    
    const onReadAll = () => {
      if (isOnlineRef.current) {
        fetchUnreadCount.current();
      }
    };
    
    window.addEventListener('localStorageDataUpdate', onStorageUpdate);
    window.addEventListener('notifications:read-all', onReadAll);
    
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('localStorageDataUpdate', onStorageUpdate);
      window.removeEventListener('notifications:read-all', onReadAll);
      
      // Cancel any pending request on cleanup
      if (unreadRequestRef.current) {
        try {
          unreadRequestRef.current.abort();
        } catch (e) {
          // ignore
        }
      }
    };
  }, []);

  // Open SSE stream for live notifications
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    let isCancelled = false;
    let reconnectTimeout = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;
    const baseReconnectDelay = 3000;

    const connect = async () => {
      if (isCancelled) return;
      
      // Cancel any existing SSE connection
      if (sseControllerRef.current) {
        try {
          sseControllerRef.current.abort();
        } catch (e) {
          // ignore
        }
      }

      const controller = new AbortController();
      sseControllerRef.current = controller;

      try {
        const response = await getRequestSSE(`/api/notifications/sse`, { signal: controller.signal });
        if (!response) throw new Error('SSE failed');
        
        // Reset reconnect attempts on successful connection
        reconnectAttempts = 0;
        
        let buffer = '';
        for (;;) {
          if (response.done || isCancelled) break;
          buffer += response.value;
          let sepIndex;
          while ((sepIndex = buffer.indexOf('\n\n')) !== -1) {
            const rawEvent = buffer.slice(0, sepIndex);
            buffer = buffer.slice(sepIndex + 2);
            const lines = rawEvent.split('\n');
            const dataLines = lines.filter(l => l.startsWith('data:'))
              .map(l => l.replace(/^data:\s?/, ''));
            if (dataLines.length === 0) continue;
            const dataStr = dataLines.join('\n');
            try {
              const payload = JSON.parse(dataStr);
              
              // Handle different payload types
              if (payload && payload.type === 'heartbeat') {
                continue; // Skip heartbeat messages
              }
              
              if (payload && payload.type === 'connection') {
                continue; // Skip connection messages
              }
              
              if (payload && payload.type === 'notifications') {
                const notifications = payload.notifications || [];
                if (notifications.length === 0) continue;
                
                // Create hash for this notification batch
                const notificationHash = createNotificationHash(notifications);
                
                // Check if we've already processed this exact notification batch
                if (notificationHash === lastNotificationHashRef.current) {
                  continue; // Skip duplicate notification batches
                }
                
                // Update the hash to mark this batch as processed
                lastNotificationHashRef.current = notificationHash;
                
                // Process only new notifications (not already processed)
                const newNotifications = notifications.filter(notification => {
                  const notificationId = notification._id;
                  if (processedNotificationsRef.current.has(notificationId)) {
                    return false; // Already processed
                  }
                  processedNotificationsRef.current.add(notificationId);
                  return true;
                });
                
                if (newNotifications.length > 0) {
                  // Fetch actual unread count from API
                  if (isOnlineRef.current) {
                    fetchUnreadCount.current();
                  }
                  
                  // Show toast notification for the first new notification
                  const firstNotification = newNotifications[0];
                  if (firstNotification?.title || firstNotification?.body) {
                    notification.info({
                      message: firstNotification.title || 'New Notification',
                      description: firstNotification.body || '',
                      placement: 'topRight',
                      duration: 3,
                      style: {
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                      },
                    });
                  }
                  
                  // broadcast to modal/listeners
                  window.dispatchEvent(new CustomEvent('notifications:new', { detail: newNotifications }));
                }
              }
            } catch (err) {
              // ignore JSON parse errors
            }
          }
        }
      } catch (err) {
        if (controller.signal.aborted || isCancelled) return;
        
        // Implement exponential backoff for reconnection
        reconnectAttempts++;
        if (reconnectAttempts <= maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts - 1);
          reconnectTimeout = setTimeout(() => {
            if (!isCancelled && isOnlineRef.current) {
              connect();
            }
          }, delay);
        }
      }
    };

    connect();

    const onOnline = () => {
      isOnlineRef.current = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      reconnectAttempts = 0;
      connect();
    };

    const onOffline = () => {
      isOnlineRef.current = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    return () => {
      isCancelled = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      try { 
        if (sseControllerRef.current) {
          sseControllerRef.current.abort();
        }
      } catch {}
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (width <= 1024) {
      setIsMenuOpen(false);
    }
  }, [width]);

  // Don't render anything while checking authentication
  if (isLoading || !isAuthenticated) {
    return null;
  }

  return (
    <header className="flex items-center h-14 bg-bgnav justify-between px-6 !text-black sticky top-0 z-50 bg-primary py-10">
      {/* Logo */}
      <img
        src={Logo}
        className="object-contain h-18 w-auto rounded-full p-2"
        alt="Logo"
      />

      {/* Desktop NavLinks */}
      <div className="hidden lg:block">
        <Navlinks links={navlinks} isAdmin={true} />
      </div>

      <div className="flex items-center gap-2">
        {/* Toggle Menu Button (Mobile) */}
        <div className="lg:hidden block text-black mt-2">
          <button onClick={toggleMenu} aria-label="Toggle menu">
            {isMenuOpen ? (
              <X className="h-5 w-5 cursor-pointer text-white" />
            ) : (
              <Menu className="h-5 w-5 cursor-pointer text-white" />
            )}
          </button>
        </div>

        {/* Notifications */}
        <div className="h-8 w-8 rounded-full flex justify-center items-center relative bg-[#193E6A] text-white cursor-pointer group" onClick={() => setIsNotificationOpen(true)}>
          <Bell className="h-4 w-4 group-hover:rotate-12 transition-transform duration-150 ease-linear" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-red-500 text-[10px] leading-4 text-white flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>

        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <div className="cursor-pointer h-12 w-12 rounded-full overflow-hidden flex items-center justify-center bg-gray-200">
            {data?.logo ? (
              <img 
                src={data.logo} 
                alt="User Avatar" 
                className="h-full w-full object-cover"
              />
            ) : (
              <UserAvatar />
            )}
          </div>
        </Dropdown>
      </div>

      {/* Mobile Menu Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-10 transition-opacity duration-300 lg:hidden ${
          isMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-[75%] bg-primary p-4 shadow-md z-20 transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Top Bar with Logo and Close Button */}
        <div className="flex items-center justify-between mb-6">
          <img
            src={Logo}
            alt="Logo"
            className="h-10 w-auto object-contain rounded-full"
          />
          <button onClick={toggleMenu} aria-label="Close menu">
            <X className="h-6 w-6 text-white" />
          </button>
        </div>

        {/* Mobile Nav Links */}
        <Navlinks links={navlinks} isAdmin={true} />
      </div>

      {/* Notification Modal */}
      <NotificationModal
        userId={data.id}
        open={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={isChangePasswordOpen}
        onClose={() => setIsChangePasswordOpen(false)}
      />
    </header>
  );
};

export default Navbar;
